import React from "react";
import { PolyIcon } from "../../elements/PolyIcon";
import "./StepProgressIndicator.scss";

export interface ProgressStep {
  title: string;
  subtitle?: string;
}

/**
 * Displays a vertical list of steps, each with potential "sub-information",
 * indicating the current step, and upcoming steps.
 *
 * @param steps - array of step objects (see ProgressStep type above)
 * @param currentStep - 0-based index of the current step
 * @param {boolean} fadeOut - indicates whether the future step "balls" should decrease in size.
 *                            (Currently only supports up to 4 future steps)
 * @constructor
 */
export const StepIndicator: React.FC<{
  steps: Array<ProgressStep>;
  currentStep: number;
  fadeOut?: boolean;
}> = ({ steps, currentStep, fadeOut = false }) => {
  const classes = ["step-indicator"];
  if (fadeOut) {
    classes.push("fadeout");
  }

  return (
    <div className={classes.join(" ")}>
      {steps.map((step, index) => {
        const classes = ["step", `step-${index}`];
        let icon = null;

        if (index < currentStep) {
          classes.push("step-before");
          icon = <PolyIcon icon="checkmark" />;
        }

        if (index === currentStep) {
          classes.push("step-current");
        }

        if (index > currentStep) {
          classes.push("step-after");
        }

        return (
          <div key={index} className={classes.join(" ")}>
            <span className="step-ball-container">
              <span className="step-ball">{icon}</span>
            </span>
            <div className="step-label">
              {step.title}
              {step.subtitle && (
                <div className="step-subtitle">{step.subtitle}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
